import { AfterViewChecked, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { StatefulService } from '@app/service/stateful.service';
import { DictionaryService } from '@cudirect/shared-ui-library';
import { CardInstance } from '@workflow/class/card.service';
import { FlowInstance } from '@workflow/class/flow.service';
import { PageInstance } from '@workflow/class/page.service';
import { AmortizationTypes, LoanPurposeTypes } from '@workflow/ecp-shared/enum/enum';
import { CoApplicantLegacyHelper } from '@workflow/ecp-workflow/dynamic-app/service/helpers/co-applicant-legacy.helper';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';

/** Const Variable for Accounts & Services Page Id */
const ACCOUNTS_AND_SERVICES = 'oao-additional-accounts';

/** OAO Flow base path */
const OAO_FLOW_BASE_PATH = 'oao/';

/**
 * different type of field layout
 */
const BENEFICIARY = 'api:oaoMemberInformation/beneficiary';
const BENEFICIARY_ID = 'oao-beneficiary';
@Component({
  selector: 'ecp-review-page',
  templateUrl: './review.component.html'
})
export class ReviewComponent implements OnInit, AfterViewChecked {
  /** instance to legacy co app page helper */
  public readonly _coApp: CoApplicantLegacyHelper;

  /**
   * list of pages to show in review
   */
  @Input() public workflowPages: PageInstance[];

  /**
   * Hide review page based on config
   */
  @Input() public hideOptionalReviewPages: boolean;

  /**
   * current workflow
   */
  @Input() public flow: FlowInstance;

  @Input() public editUrlFn: (page: PageInstance, flow: FlowInstance, card: CardInstance) => void;

  /** event emitter */
  @Output() public validateEvent = new EventEmitter();
  /**
   * workFlowId
   */
  public workFlowId: string;

  /**
   * loanPurposeType_HELOC
   */
  public loanPurposeType_HELOC = LoanPurposeTypes.HELOC;

  /**
   * loanPurposeType_Purchase
   */
  public loanPurposeType_Purchase = LoanPurposeTypes.Purchase;

  /**
   * loanPurposeType_Refinance
   */
  public loanPurposeType_Refinance = LoanPurposeTypes.Refinance;

  /**
   * Label additional requirements
   */
  public lbladditionalrequirements = 'additional-requirements';

  /**
   * Label Purchase additional requirements
   */
  public lblPurchaseAdditionalRequirements = 'additional-requirements-purchase';

  /**
   * Label Refinance additional requirements
   */
  public lblRefinanceAdditionalRequirements = 'additional-requirements-refinance';

  /**
   * isARMAmortizationType
   */
  public isARMAmortizationType = false;

  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly route: ActivatedRoute,
    public dictionary: DictionaryService,
    public readonly workflowService: WorkflowService
  ) {
    // eslint-disable-next-line no-underscore-dangle
    this._coApp = new CoApplicantLegacyHelper();
  }

  /**
   * hash anchor for redirecting
   */
  public fragment: string;
  public beneficiarySize: number;
  public isScrolled = false;

  /** Check the page to show in current review page */
  public showInReview(page: PageInstance): boolean {
    if (page.reviewPageIds.length > 0) {
      return page.reviewPageIds?.includes(this.workflowService.currentPage.id);
    }
    return true;
  }

  public ngOnInit(): void {
    // Setting isAuthorizedForCreditPull back to true if the data gets removed.  Can be removed later, since this is fix for API issue.
    if (this.statefulService.wrap('ss:authorized-credit').value) {
      this.statefulService.wrap('api:borrowerInfo/isAuthorizedForCreditPull').value = true;
    }
    this.beneficiarySize = this.statefulService.arraySize(BENEFICIARY);

    this.route.fragment.subscribe((fragment) => {
      this.fragment = fragment;
    });

    this.workFlowId = this.statefulService.wrap('api:loan/loanInformation/loanType').value as string;
    const amortizationType = this.statefulService.wrap('api:loan/loanProduct/amortizationType').value as string;
    this.isARMAmortizationType = amortizationType === AmortizationTypes.ARM;
  }

  public reviewablePages(): PageInstance[] {
    // eslint-disable-next-line no-underscore-dangle
    return this._coApp.reviewablePages(this.workflowService.currentFlow, this.hideOptionalReviewPages);
  }

  public ngAfterViewChecked(): void {
    if (this.fragment && document.querySelector('#' + this.fragment) && !this.isScrolled) {
      setTimeout(() => {
        document.querySelector('#' + this.fragment)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusId = `edit-${this.fragment}`;
        document.getElementById(focusId)?.focus({ preventScroll: true });
        this.isScrolled = true;
      }, 100);
    }
  }

  public getReviewCards(page: PageInstance): CardInstance[] {
    return page.cards.filter((card) => card.visible && !card.hideInReview);
  }

  /** Property to show account and service page in review */
  public showAccountServiceSection(): boolean {
    const AccountsPages = this.workflowService.currentFlow.pages.filter((t) => t.id.includes(ACCOUNTS_AND_SERVICES));
    const AccountsPage = AccountsPages ? AccountsPages[0] : null;
    return AccountsPage && this.showInReview(AccountsPage);
  }

  /** showing Beneficiary Asset Allocation on review page */
  public showBeneficiaryReview(id: string): boolean {
    return id && id === BENEFICIARY_ID && this.beneficiarySize > 0;
  }
  public get isOLALoan(): boolean {
    return this.flow?.id === 'ola' || this.flow?.id.indexOf('ola-consumer') > -1;
  }

  /**
   * Check, Can we show Accounts & Services Section at Review Page based on flow.
   * OAO - We have to display, OLA - No need to display
   * @returns Check
   */
  public canShowAccountsandServicesSection(): boolean {
    const showInReviewPage = this.showAccountServiceSection();
    return this.workflowService.currentFlow.id.indexOf(OAO_FLOW_BASE_PATH) > -1 && showInReviewPage;
  }
}
